import {AccountsService} from "app/account/services/AccountsService";
import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {AccountBriefRecord, AccountBriefRecords, AccountIds} from "../types/AccountBriefRecord";
import {CurrentUserService} from "../../services/user/CurrentUserService";
import {PortfolioType} from "../../portfolio/models/Portfolio";
import {AccountSource} from "../types/AccountSource";
import {ClientEventService} from "../../services/client-events/ClientEventService";
import {ClientEventType} from "../../services/client-events/models/ClientEventType";
import {RecentPortfolioService} from "../../services/favourite-portfolios/RecentPortfolioService";
import {isNotNull} from "../../js/util/UtilFunctions";

const UpdateIntervalSeconds = 60 * 5;
const UpdateInterval = UpdateIntervalSeconds * 1000;
export class AccountStore implements AccountSource {

    public static readonly id = "accountStore";

    private readonly loadAccounts$ = new BehaviorSubject(true);
    private readonly accounts$ = new ReplaySubject<AccountBriefRecords>(1);
    private readonly loadOwnedAccounts$ = new BehaviorSubject(true);
    private readonly ownedAccountIds$ = new ReplaySubject<AccountIds>(1);
    private readonly currentAccountId$ = new ReplaySubject<number>(1);
    private readonly currentAccount$ = new ReplaySubject<AccountBriefRecord | null>(1);
    private readonly userAccounts$ = new ReplaySubject<AccountBriefRecords>(1);

    /** @ngInject */
    public constructor(private readonly accountService: AccountsService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly clientEventService: ClientEventService,
                       private readonly recentPortfolioService: RecentPortfolioService) {
        this.init();
    }

    private init() {
        this.watchUserAccountSources();
        this.watchCurrentAccount();
        this.watchDefaultAccountSources();
        this.watchLoadAccounts();
        this.watchLoadOwnedAccounts();
        this.watchReloadEvents();
        this.watchRecentAccounts();
    }

    private watchUserAccountSources() {
        const source$ = Observable.combineLatest(this.accounts$, this.ownedAccountIds$);
        source$.observeOn(Scheduler.async)
               .map(([accounts, ownedAccountIds]) => this.usersAccounts(accounts, ownedAccountIds))
               .subscribe((accounts) => this.userAccounts$.next(accounts));
    }

    private usersAccounts(portfolios: AccountBriefRecords, ownedIds: AccountIds): AccountBriefRecords {
        return portfolios.filter((p) => ownedIds.includes(p.id));
    }

    private watchCurrentAccount() {
        Observable.combineLatest(this.accounts$, this.currentAccountId$)
                  .map(([portfolios, currentId]) => portfolios.find((p) => p.id === currentId) || null)
                  .subscribe((portfolio) => this.currentAccount$.next(portfolio));
    }

    private watchDefaultAccountSources() {
        const currentAccount = this.currentAccount$.startWith(null);
        this.userAccounts$
            .observeOn(Scheduler.async)
            .withLatestFrom(currentAccount)
            .filter(([accounts, selected]) => selected == null)
            .map(([accounts, _]) => accounts)
            .map((accounts) => this.selectDefaultAccount(accounts))
            .subscribe((account) => {
                if (account == null) {
                    this.currentAccount$.next(null);
                } else {
                    this.currentAccountId$.next(account.id);
                }
            });
    }

    private selectDefaultAccount(accounts: AccountBriefRecords): AccountBriefRecord | null {
        const firstBroker = accounts.find((a) => a.portfolioType === PortfolioType.BROKER);
        if (firstBroker != null) {
            return firstBroker;
        } else {
            return accounts[0];
        }
    }

    private watchLoadAccounts() {
        this.loadAccounts$
            .throttleTime(UpdateInterval) // TODO shouldn't throttle here but have to due to excessive updates from server
            .observeOn(Scheduler.async)
            .switchMap(() => this.retrieveAccounts())
            .subscribe((accounts) => this.accounts$.next(accounts));
    }

    private watchLoadOwnedAccounts() {
        this.loadOwnedAccounts$
            .observeOn(Scheduler.async)
            .switchMap(() => this.retrieveOwnedAccounts())
            .subscribe((accountIds) => this.ownedAccountIds$.next(accountIds));
    }

    private watchReloadEvents() {
        const reloadEvents = [ClientEventType.POSITIONS_LOADED, ClientEventType.PORTFOLIO_UPDATED];
        this.clientEventService
            .events()
            .filter((e) => reloadEvents.includes(e.type))
            .auditTime(100)
            .subscribe(() => this.loadAccounts$.next(true));
    }

    private watchRecentAccounts() {
        this.currentAccount$
            .filter(isNotNull)
            .map((account) => ({
                id: account.id,
                accountName: account.name
            }))
            .subscribe((account) => this.recentPortfolioService.openPortfolio(account));
    }

    private retrieveAccounts(): Observable<AccountBriefRecords> {
        return Observable.fromPromise(this.accountService.list())
                         .catch(() => Observable.empty<AccountBriefRecords>());
    }

    private retrieveOwnedAccounts(): Observable<AccountIds> {
        return Observable.fromPromise(this.accountService.ownedAccounts())
                         .catch(() => Observable.empty<AccountIds>());
    }

    public selectAccount(id: number) {
        this.currentAccountId$.next(id);
    }

    public get accounts(): Observable<AccountBriefRecords> {
        return this.accounts$.observeOn(Scheduler.async);
    }

    public get userAccounts(): Observable<AccountBriefRecords> {
        return this.userAccounts$.observeOn(Scheduler.async);
    }

    public get current(): Observable<AccountBriefRecord | null> {
        return this.currentAccount$
                   .observeOn(Scheduler.async)
                   .distinctUntilChanged((a, b) => {
                       if ((a == null) || (b == null)) {
                           return a === b;
                       } else {
                           return a.id === b.id;
                       }
                   });
    }

    public refresh() {
        this.loadAccounts$.next(true);
    }
}
